import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, ScrollView, TextInput } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import CreateStandUpActionPlanForm from '../components/CreateStandUpActionPlan';
import { TopOpportunityItem } from '../components/TopOpportunitiesTable';

interface CardData {
  module_name?: string; // For lighthouse tasks
  title: string; // For all card types
  description?: string;
  kfa?: string; // For lighthouse tasks
  kfa_id?: number; // For lighthouse tasks
  card_id?: string; // For KPI cards
  id?: string; // For KPI cards
  [key: string]: any;
}

export interface StandUpCreateActionPlanScreenProps {
  cardData: CardData;
  standupSessionId: string;
  standupId?: string;
  isEditMode?: boolean;
  onBack: () => void;
  onSubmit: (data: any) => Promise<void>;
  isContinuousImprovement?: boolean;
  isLighthouseExecution?: boolean;
  isSparcKpi?: boolean;
}

export const StandUpCreateActionPlanScreen: React.FC<StandUpCreateActionPlanScreenProps> = ({
  cardData,
  standupSessionId,
  standupId,
  isEditMode,
  onBack,
  onSubmit,
  isContinuousImprovement,
  isLighthouseExecution,  isSparcKpi = false,}) => {

  // Map card data to action plan format - memoized to prevent recreating on every render
  // Separate logic for Lighthouse Execution vs Lighthouse Tasks vs SPARC KPI
  // Note: cardData object reference is included to ensure fresh computation when switching sections
  const actionPlanData: TopOpportunityItem = React.useMemo(() => {
    if (isLighthouseExecution) {
      // Lighthouse Execution - uses LighthouseKpiCard data
      return {
        ID: 0,
        TITLE: 'Title',
        SOURCE: 'Lighthouse Execution', // Fixed source for Lighthouse Execution
        TYPE: 'Process', // Pre-select Process for Lighthouse Execution (editable)
        AREA: cardData?.title || '', // LighthouseKpiCard title as Area
        OWNER: '',
        ASSIGNEE: '',
        EMPLOYEE: '',
        START_DATE: cardData?.startDate || '',
        TARGET_DATE: cardData?.targetDate || '',
        ACTION_PLAN: '',
        NOTES: '',
        STATUS: 'In Progress',
        PRIORITY: '',
        OWNER_GPID: '',
        ASSIGNEE_GPID: '',
        capabilities: cardData?.question_id ? [{
          question_id: cardData.question_id,
          question_title: cardData.title || '',
          is_parent: cardData.is_parent ?? false,
        }] : [], // Send LighthouseKpiCard question_id in capabilities
      };
    }
    
    if (isSparcKpi) {
      // SPARC KPI - uses SparcKpiCard data
      return {
        ID: 0,
        TITLE: 'Title',
        SOURCE: 'SPARC KPI', // Fixed source for SPARC KPI
        TYPE: 'KPI', // Pre-select KPI for SPARC KPI cards (editable)
        AREA: cardData?.title || '', // SparcKpiCard title as Area
        OWNER: '',
        ASSIGNEE: '',
        EMPLOYEE: '',
        START_DATE: cardData?.startDate || '',
        TARGET_DATE: cardData?.targetDate || '',
        ACTION_PLAN: '',
        NOTES: '',
        STATUS: 'In Progress',
        PRIORITY: '',
        OWNER_GPID: '',
        ASSIGNEE_GPID: '',
        capabilities: (cardData?.id || cardData?.card_id) ? [{
          question_id: cardData.id || cardData.card_id,
          question_title: cardData.title || '',
          is_parent: false,
        }] : [], // Send SparcKpiCard ID in capabilities
      };
    }
    
    // Lighthouse Task logic - uses actual lighthouse task data
    return {
      ID: 0,
      TITLE: 'Title',
      SOURCE: cardData?.module_name || '', // Pre-populate with Lighthouse container name
      TYPE: 'KFA', // Pre-select KFA for Lighthouse tasks
      AREA: cardData?.kfa || '', // Pre-filled with kfa (description from API)
      OWNER: '',
      ASSIGNEE: '',
      EMPLOYEE: '',
      START_DATE: cardData?.startDate || '', // Pre-populated from standup date
      TARGET_DATE: cardData?.targetDate || '', // Pre-populated from standup date
      ACTION_PLAN: '',
      NOTES: '',
      STATUS: 'In Progress',
      PRIORITY: '',
      OWNER_GPID: '',
      ASSIGNEE_GPID: '',
      capabilities: cardData?.kfa_id ? [{
        question_id: cardData.kfa_id,
        question_title: cardData.kfa || '', // Use kfa (description) for question_title
        is_parent: false,
      }] : [], // Send lighthouse task id as question_id with is_parent: false
    };
  }, [cardData, isLighthouseExecution, isSparcKpi]);

  return (
    <View style={styles.page}>
      <ScrollView 
        contentContainerStyle={styles.scrollContent}
        scrollEnabled={true}
        bounces={true}
      >
        {/* Header */}
        <View style={styles.headerWrapper}>
          <Text style={styles.heading}>Create Action Plan</Text>
        </View>

        {/* Action Plan Form */}
        <View style={styles.formWrapper}>
          <CreateStandUpActionPlanForm
            actionPlanData={actionPlanData}
            standupSessionId={standupSessionId}
            standupId={standupId}
            isEditMode={isEditMode}
            type="create"
            onSubmit={onSubmit}
            onCancel={onBack}
            isContinuousImprovement={isContinuousImprovement}
            isLighthouseExecution={isLighthouseExecution}
            isSparcKpi={isSparcKpi}
          />
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: Platform.OS === 'web' ? 24 : 16,
    backgroundColor: '#F5F9FF',
  },
  scrollContent: {
    alignItems: 'center',
    paddingBottom: 24,
  },
  headerWrapper: {
    display: 'flex',
    width: Platform.OS === 'web' ? '70%' : '100%',
    maxWidth: 900,
    alignSelf: 'flex-start',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginBottom: 24,
    marginTop: 0,
  },
  heading: {
    color: '#2D2D2D',
    ...(Platform.OS === 'web' ? { fontFeatureSettings: "'liga' 0, 'clig' 0" } : {}),
    fontFamily: 'Inter',
    fontSize: Platform.OS === 'web' ? 28 : 24,
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 40,
    paddingLeft: Platform.OS === 'web' ? 16 : 0,
  } as any,
  titleSection: {
    width: Platform.OS === 'web' ? '70%' : '100%',
    maxWidth: 900,
    alignSelf: 'flex-start',
    paddingLeft: Platform.OS === 'web' ? 16 : 0,
    marginBottom: 16,
  },
  titleDisplayContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  titleText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2D2D2D',
    fontFamily: 'Inter',
    flex: 1,
  },
  editIconButton: {
    padding: 4,
  },
  titleEditContainer: {
    width: '100%',
  },
  titleInput: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2D2D2D',
    fontFamily: 'Inter',
    borderWidth: 1,
    borderColor: '#005CBC',
    borderRadius: 4,
    padding: 8,
    backgroundColor: '#FFFFFF',
    minHeight: 44,
  },
  formWrapper: {
    width: Platform.OS === 'web' ? '70%' : '100%',
    maxWidth: 900,
    alignSelf: 'flex-start',
    paddingLeft: Platform.OS === 'web' ? 16 : 0,
  },
});

export default StandUpCreateActionPlanScreen;
